import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { usePlaidLink } from 'react-plaid-link';
import BottomDrawer from '../Drawer/Drawer';
import Button from '../Button/Button';
import { ReactComponent as ErrorWarningIcon } from '../../assets/images/svg/error-warning.svg';
import { ReactComponent as CloseIcon } from '../../assets/images/svg/close.svg';
import { ReactComponent as AlertTriangleIcon } from '../../assets/images/svg/alert-triangle.svg';
import { ReactComponent as BankIcon } from '../../assets/images/svg/bank.svg';
import { ReactComponent as GreenCheckIcon } from '../../assets/images/svg/green_check.svg';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import { mediaBelow } from '../../assets/styles/helper';
import { selectLinkToken } from '../../store/selectors/plaidSelectors';
import { fetchLinkToken } from '../../store/actions/plaid/PlaidActions';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import { checkPlaidMismatch } from '../../request/plaidRequest';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const DrawerTopRow = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
`;

const DrawerCloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);

  > svg {
    width: 20px;
    color: ${themeColors.colorTextDark};
  }
`;

const CautionIconWrapper = styled.div`
  width: 62px;
  height: 62px;
  border-radius: 50%;
  background-color: ${themeColors.colorIconBackground};
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 36px;
    height: 36px;
    color: ${themeColors.colorWarningText};
  }
`;

const Title = styled.h2`
  font-family: ${variables.family.goldSilverHeaders};
  font-size: 14px;
  font-weight: 600;
  font-style: normal;
  line-height: 100%;
  letter-spacing: 0px;
  text-align: center;
  vertical-align: middle;
  color: ${themeColors.colorTextPrimary};
  margin: 0;
`;

const Description = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0px;
  color: ${themeColors.colorTextSecondary};
  text-align: left;
  margin: 0;
`;

const getBankDetailsBg = ({ $valid, $error }) => {
  if ($valid) return themeColors.colorMismatchValidBackground;
  if ($error) return themeColors.colorMismatchErrorBackground;
  return themeColors.colorPrimaryLight;
};

const getBankDetailsBorder = ({ $valid, $error }) => {
  if ($valid) return `1px solid ${themeColors.colorMismatchValidBorder}`;
  if ($error) return `1px dashed ${themeColors.colorMismatchErrorBorder}`;
  return `1px solid ${themeColors.colorCardBorder}`;
};

const BankDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background-color: ${getBankDetailsBg};
  border: ${getBankDetailsBorder};
  border-radius: 4px;
  width: 100%;
  min-height: 52px;
  box-sizing: border-box;

  > svg {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    color: ${themeColors.colorTextPrimary};
  }
`;

const BankTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BankName = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  font-weight: 500;
  line-height: 140%;
  letter-spacing: 0%;
  color: ${themeColors.colorTextDark};
`;

const BankMask = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0%;
  color: ${themeColors.colorTextSecondary};
`;

const CautionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: ${themeColors.colorWarningBackground};
  border-left: 5px solid ${themeColors.colorWarningText};
  width: 100%;
  min-height: 52px;
  box-sizing: border-box;

  > svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: ${themeColors.colorWarningText};
  }
`;

const CautionText = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0%;
  color: ${themeColors.colorWarningText};
  margin: 0;
`;

const SmallText = styled.p`
  font-size: 12px;
  line-height: 19px;
  color: ${themeColors.colorTextSecondary};
  text-align: center;
  margin: 0;
`;

const ReauthButton = styled(Button)`
  font-family: ${variables.family.goldSilver} !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  line-height: 100% !important;
  letter-spacing: 0px !important;
  height: 44px !important;
  text-transform: capitalize !important;
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 12px;

  > button {
    flex: 1;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    flex-direction: column;

    > button {
      flex: initial;
      width: 100%;
    }
  }
`;

const PhoneLink = styled.a`
  color: ${themeColors.colorPrimary};
  text-decoration: none;
`;

const SuccessIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 62px;
    height: 62px;
  }
`;

const SuccessMessage = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 16px;
  font-weight: 500;
  line-height: 130%;
  letter-spacing: 0%;
  color: ${themeColors.colorTextPrimary};
  text-align: center;
  margin: 0;
`;

const MismatchLabel = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 400;
  line-height: 19px;
  color: ${themeColors.colorTextSecondary};
  margin: 0;
`;

const MismatchBankName = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  font-weight: 500;
  color: ${themeColors.colorTextDark};
`;

const MismatchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const MismatchStatusIcon = styled.div`
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  > svg {
    width: 20px;
    height: 20px;
    color: ${({ $error }) =>
      $error ? themeColors.colorError : themeColors.colorSuccess};
  }
`;

const OptionCard = styled.div`
  border: 1px solid ${themeColors.colorBorderPrimary};
  border-radius: 4px;
  padding: 10px 9px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;
  margin-top: 8px;
`;

const OptionTitle = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 600;
  font-style: normal;
  line-height: 100%;
  letter-spacing: 0px;
  vertical-align: middle;
  color: ${themeColors.colorTextSecondary};
  margin: 0;
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: ${themeColors.colorBorderPrimary};
  }
`;

const OrText = styled.span`
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  color: ${themeColors.colorTextSecondary};
  flex-shrink: 0;
`;

const HowToFixTitle = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 600;
  font-style: normal;
  line-height: 100%;
  letter-spacing: 0px;
  vertical-align: middle;
  color: ${themeColors.colorTextPrimary};
  margin: 0;
`;

const HowToFixStep = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 12px;
  font-weight: 400;
  font-style: normal;
  line-height: 16px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: ${themeColors.colorTextSecondary};
  margin: 0;
`;

const PlaidReauthDrawer = ({
  open,
  onClose,
  onChangePayment,
  onReauthSuccess,
  selectedPaymentMethod,
  buyErrorMessage,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const linkToken = useSelector(selectLinkToken);
  const settings = useSelector(selectSettings);
  const [reAuthSuccess, setReAuthSuccess] = useState(false);
  const [reAuthMismatch, setReAuthMismatch] = useState(null);
  const [reAuthMismatchError, setReAuthMismatchError] = useState(false);

  const bankAccountUid = selectedPaymentMethod?.PaymentMethodUid;
  const pendingMismatchErrorRef = useRef(false);

  useEffect(() => {
    setReAuthMismatchError(false);
    pendingMismatchErrorRef.current = false;
  }, [bankAccountUid]);

  useEffect(() => {
    setReAuthSuccess(false);
    setReAuthMismatch(null);
    if (open) {
      if (pendingMismatchErrorRef.current) {
        setReAuthMismatchError(true);
        pendingMismatchErrorRef.current = false;
      }
      dispatch(fetchLinkToken({ BankAccountUid: bankAccountUid }));
    }
  }, [open, dispatch, bankAccountUid]);

  const onPlaidSuccess = useCallback(
    async (_publicToken, metadata) => {
      const plaidBankAccountId = metadata?.accounts?.[0]?.id;
      try {
        const { data: isMismatch } = await checkPlaidMismatch({
          BankAccountUid: bankAccountUid,
          PlaidBankAccountId: plaidBankAccountId,
        });
        if (isMismatch) {
          setReAuthMismatch({
            bankName: metadata?.institution?.name,
            mask: metadata?.accounts?.[0]?.mask,
          });
        } else {
          setReAuthSuccess(true);
        }
      } catch {
        setReAuthSuccess(true);
      }
    },
    [bankAccountUid],
  );

  const onPlaidExit = useCallback(
    (err) => {
      if (err != null && err.error_code === 'INVALID_LINK_TOKEN') {
        dispatch(fetchLinkToken({ bankAccountUid }));
      }
    },
    [dispatch, bankAccountUid],
  );

  const { open: openPlaid } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
  });

  const bankName = selectedPaymentMethod?.BankName;
  const last4 = selectedPaymentMethod?.BankAccountMask ?? '';
  const supportPhone = settings?.SupportPhoneTollFree || '(888) 319-8166';
  const supportPhoneRaw = supportPhone.replace(/\D/g, '');

  if (reAuthMismatchError || buyErrorMessage) {
    return (
      <BottomDrawer
        isOpen={open}
        onClose={onClose}
        maxHeight="90%"
        contentPadding="16px"
        hideClose
      >
        <Wrapper>
          <DrawerTopRow>
            <CautionIconWrapper>
              <AlertTriangleIcon />
            </CautionIconWrapper>
            <DrawerCloseButton type="button" onClick={onClose}>
              <CloseIcon />
            </DrawerCloseButton>
          </DrawerTopRow>

          <Title>{t('buyWizard.postPaid.plaidMismatchErrorTitle')}</Title>

          <MismatchSection>
            <HowToFixTitle>
              {t('buyWizard.postPaid.plaidReauthMismatchHowToFixTitle')}
            </HowToFixTitle>

            <OptionCard>
              <OptionTitle>
                {t('buyWizard.postPaid.plaidMismatchErrorOption1Label')}
              </OptionTitle>
              <HowToFixStep>
                {t('buyWizard.postPaid.plaidReauthMismatchStep1', { last4 })}
              </HowToFixStep>
              <HowToFixStep>
                {t('buyWizard.postPaid.plaidReauthMismatchStep2')}
              </HowToFixStep>
            </OptionCard>

            <OrDivider>
              <OrText>{t('buyWizard.postPaid.plaidMismatchErrorOr')}</OrText>
            </OrDivider>

            <OptionCard>
              <OptionTitle>
                {t('buyWizard.postPaid.plaidMismatchErrorOption2Label')}
              </OptionTitle>
              <HowToFixStep>
                {t('buyWizard.postPaid.plaidMismatchErrorOption2Text')}
              </HowToFixStep>
            </OptionCard>
          </MismatchSection>

          <ButtonsWrapper>
            <ReauthButton variant="primaryOutlined" size="sm" onClick={onClose}>
              {t('buyWizard.postPaid.plaidReauthMismatchCta')}
            </ReauthButton>
          </ButtonsWrapper>

          <SmallText>
            {t('buyWizard.postPaid.plaidReauthHelpText')}{' '}
            <PhoneLink href={`tel:${supportPhoneRaw}`}>
              {supportPhone}
            </PhoneLink>
          </SmallText>
        </Wrapper>
      </BottomDrawer>
    );
  }

  if (reAuthSuccess) {
    return (
      <BottomDrawer
        isOpen={open}
        onClose={onClose}
        maxHeight="90%"
        contentPadding="16px"
        hideClose
      >
        <Wrapper>
          <SuccessIconWrapper>
            <GreenCheckIcon />
          </SuccessIconWrapper>
          <SuccessMessage>
            {t('buyWizard.postPaid.plaidReauthSuccessMessage')}
          </SuccessMessage>
          <ButtonsWrapper>
            <ReauthButton
              variant="primaryOutlined"
              size="sm"
              onClick={() => {
                onClose();
                if (onReauthSuccess) onReauthSuccess();
              }}
            >
              {t('buyWizard.postPaid.plaidReauthSuccessCta')}
            </ReauthButton>
          </ButtonsWrapper>
        </Wrapper>
      </BottomDrawer>
    );
  }

  if (reAuthMismatch) {
    return (
      <BottomDrawer
        isOpen={open}
        onClose={onClose}
        maxHeight="90%"
        contentPadding="16px"
        hideClose
      >
        <Wrapper>
          <DrawerTopRow>
            <CautionIconWrapper>
              <AlertTriangleIcon />
            </CautionIconWrapper>
            <DrawerCloseButton
              type="button"
              onClick={() => {
                pendingMismatchErrorRef.current = true;
                onClose();
              }}
            >
              <CloseIcon />
            </DrawerCloseButton>
          </DrawerTopRow>

          <Title>{t('buyWizard.postPaid.plaidReauthMismatchTitle')}</Title>

          <MismatchSection>
            <MismatchLabel>
              {t('buyWizard.postPaid.plaidReauthMismatchNeededLabel')}
            </MismatchLabel>
            <BankDetails $valid>
              <BankIcon />
              <BankTextWrapper>
                <BankName>{bankName}</BankName>
                <BankMask>***{last4}</BankMask>
              </BankTextWrapper>
              <MismatchStatusIcon>
                <GreenCheckIcon />
              </MismatchStatusIcon>
            </BankDetails>
          </MismatchSection>

          <MismatchSection>
            <MismatchLabel>
              {t('buyWizard.postPaid.plaidReauthMismatchWrongLabel')}
            </MismatchLabel>
            <BankDetails $error>
              <BankIcon />
              <BankTextWrapper>
                <MismatchBankName>{reAuthMismatch.bankName}</MismatchBankName>
                <BankMask>***{reAuthMismatch.mask}</BankMask>
              </BankTextWrapper>
              <MismatchStatusIcon $error>
                <CloseIcon />
              </MismatchStatusIcon>
            </BankDetails>
          </MismatchSection>

          <MismatchSection>
            <HowToFixTitle>
              {t('buyWizard.postPaid.plaidReauthMismatchHowToFixTitle')}
            </HowToFixTitle>
            <HowToFixStep>
              {t('buyWizard.postPaid.plaidReauthMismatchStep1', { last4 })}
            </HowToFixStep>
            <HowToFixStep>
              {t('buyWizard.postPaid.plaidReauthMismatchStep2')}
            </HowToFixStep>
          </MismatchSection>

          <SmallText>
            {t('buyWizard.postPaid.plaidReauthMismatchFooter')}
          </SmallText>

          <ButtonsWrapper>
            <ReauthButton
              variant="primaryOutlined"
              size="sm"
              onClick={() => {
                pendingMismatchErrorRef.current = true;
                onClose();
              }}
            >
              {t('buyWizard.postPaid.plaidReauthMismatchCta')}
            </ReauthButton>
          </ButtonsWrapper>

          <SmallText>
            {t('buyWizard.postPaid.plaidReauthHelpText')}{' '}
            <PhoneLink href={`tel:${supportPhoneRaw}`}>
              {supportPhone}
            </PhoneLink>
          </SmallText>
        </Wrapper>
      </BottomDrawer>
    );
  }

  return (
    <BottomDrawer
      isOpen={open}
      onClose={onClose}
      maxHeight="100%"
      contentPadding="16px"
      hideClose
    >
      <Wrapper>
        <DrawerTopRow>
          <CautionIconWrapper>
            <ErrorWarningIcon />
          </CautionIconWrapper>
          <DrawerCloseButton type="button" onClick={onClose}>
            <CloseIcon />
          </DrawerCloseButton>
        </DrawerTopRow>

        <Title>{t('buyWizard.postPaid.plaidReauthTitle')}</Title>

        <Description>
          {t('buyWizard.postPaid.plaidReauthDescription')}
        </Description>

        <BankDetails>
          <BankIcon />
          <BankTextWrapper>
            <BankName>{bankName}</BankName>
            <BankMask>***{last4}</BankMask>
          </BankTextWrapper>
        </BankDetails>

        <CautionRow>
          <AlertTriangleIcon />
          <CautionText>
            {t('buyWizard.postPaid.plaidReauthCautionMessage')}
          </CautionText>
        </CautionRow>

        <SmallText>{t('buyWizard.postPaid.plaidReauthInstruction')}</SmallText>

        <ButtonsWrapper>
          <ReauthButton
            variant="primary"
            size="sm"
            onClick={openPlaid}
            disabled={!linkToken}
          >
            {linkToken
              ? t('buyWizard.postPaid.plaidReauthButton', { last4 })
              : t('buyWizard.postPaid.plaidReauthLoadingToken')}
          </ReauthButton>
          <ReauthButton
            variant="primaryOutlined"
            size="sm"
            onClick={onChangePayment}
          >
            {t('buyWizard.postPaid.plaidReauthChangePayment')}
          </ReauthButton>
        </ButtonsWrapper>

        <SmallText>
          {t('buyWizard.postPaid.plaidReauthRedirectMessage')}
        </SmallText>

        <SmallText>
          {t('buyWizard.postPaid.plaidReauthHelpText')}{' '}
          <PhoneLink href={`tel:${supportPhoneRaw}`}>{supportPhone}</PhoneLink>
        </SmallText>
      </Wrapper>
    </BottomDrawer>
  );
};

PlaidReauthDrawer.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onChangePayment: PropTypes.func,
  onReauthSuccess: PropTypes.func,
  buyErrorMessage: PropTypes.string,
  selectedPaymentMethod: PropTypes.shape({
    BankName: PropTypes.string,
    BankAccountMask: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    PaymentMethodUid: PropTypes.string,
  }),
};

export default PlaidReauthDrawer;
